import React, { useState, useRef, useEffect, useLayoutEffect, useCallback } from 'react';
import { FiEdit2 } from 'react-icons/fi';
import './SearchableDropdown.css';

/* ── Option types ────────────────────────────────────────────── */

export interface DropdownOption {
  value: string;
  label: string;
  /** Depth for hierarchical indentation (0 = no indent) */
  depth?: number;
  /** Group header this option belongs to (rendered as a section label) */
  group?: string;
  /** Other words people type for this option (not shown): a search for "resta" finds
   *  "Quick Service / Fast Food (QSR)" when its keywords say "restaurant". */
  keywords?: string;
  /** Shows an edit (pencil) button on this option's row when `onEditOption` is given. */
  editable?: boolean;
}

export interface SearchableDropdownProps {
  options: DropdownOption[];
  /** Selected value (single-select mode). Optional in multiSelect mode. */
  value?: string;
  /** Change handler (single-select mode). Optional in multiSelect mode. */
  onChange?: (value: string) => void;
  placeholder?: string;
  className?: string;
  /** Extra class on the outer wrapper (so parent CSS can target width, etc.) */
  wrapperClassName?: string;
  /** If true, show error styling */
  hasError?: boolean;
  disabled?: boolean;
  /** Multi-select mode: clicking an option toggles it (dropdown stays open) and
   *  selected options are highlighted. Use with selectedValues + onToggleValue
   *  instead of value + onChange. */
  multiSelect?: boolean;
  /** Selected values in multiSelect mode (pass options pre-ordered if you want
   *  selected pinned to the top). */
  selectedValues?: string[];
  /** Toggle handler in multiSelect mode. */
  onToggleValue?: (value: string) => void;
  /** multiSelect only: close the dropdown as soon as an option is picked (instead of
   *  staying open to add more). The field still holds many values — reopen to add another. */
  closeOnSelect?: boolean;
  /** Optional pinned action row shown at the TOP of the open dropdown (e.g.
   *  "+ Add new folder"). It is NOT part of the options list and is not filtered
   *  by the search box — clicking it closes the dropdown and runs onClick. */
  actionRow?: { label: React.ReactNode; onClick: () => void };
  /** Single-select only: lets the user keep what they TYPED as a new value. While the
   *  search box holds text that is not exactly an option's label, a row
   *  `label(typed)` (e.g. "+ Add “Lunch meeting”") is shown above the matches; clicking
   *  it — or Enter when nothing matches — calls onCreate(typed) and closes. */
  createRow?: { label: (typed: string) => React.ReactNode; onCreate: (typed: string) => void };
  /** Options marked `editable` get a pencil button on their row (and F2 on the
   *  highlighted one): it closes the list and calls this with the option's value. */
  onEditOption?: (value: string) => void;
  /** Optional ranking of a match against the RAW search text (spaces kept): lower
   *  comes first, Infinity hides the option. Applied before the usual starts-with order
   *  (PhoneInput: "+1 " lists the +1 countries only, the field's own country first). */
  rankOption?: (option: DropdownOption, search: string) => number;
}

/* The list opens below the field, or above it when there is more room there, and is as
   tall as the room allows (between these limits). Room = the window, cut down by any
   scrolling box the field sits in (the list would be clipped by it). */
const LIST_MIN_PX = 140;
const LIST_MAX_PX = 480;
const LIST_GAP_PX = 8;

function roomAround(el: HTMLElement): { above: number; below: number } {
  const r = el.getBoundingClientRect();
  let top = 0;
  let bottom = window.innerHeight;
  for (let p = el.parentElement; p && p !== document.body; p = p.parentElement) {
    const oy = getComputedStyle(p).overflowY;
    if (oy === 'auto' || oy === 'scroll' || oy === 'hidden') {
      const pr = p.getBoundingClientRect();
      top = Math.max(top, pr.top);
      bottom = Math.min(bottom, pr.bottom);
    }
  }
  return { above: r.top - top - LIST_GAP_PX, below: bottom - r.bottom - LIST_GAP_PX };
}

/* ── Component ───────────────────────────────────────────────── */

const SearchableDropdown: React.FC<SearchableDropdownProps> = ({
  options,
  value = '',
  onChange,
  placeholder = '-- Select --',
  className = '',
  wrapperClassName = '',
  hasError = false,
  disabled = false,
  multiSelect = false,
  selectedValues = [],
  onToggleValue,
  closeOnSelect = false,
  actionRow,
  createRow,
  onEditOption,
  rankOption,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [highlightIdx, setHighlightIdx] = useState(-1);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLDivElement>(null);
  // Where the open list goes and how tall it may be (see roomAround).
  const [placement, setPlacement] = useState<{ up: boolean; maxHeight: number } | null>(null);

  /* ── Derived: selected label ────────────────────────────────── */
  const selectedOption = options.find(o => o.value === value);
  const displayLabel = selectedOption?.label ?? '';

  /* ── Multi-select derived state ─────────────────────────────── */
  const isOptionSelected = useCallback(
    (v: string) => multiSelect ? selectedValues.includes(v) : v === value,
    [multiSelect, selectedValues, value],
  );
  const multiCount = selectedValues.length;
  const multiSummary = multiCount === 0
    ? ''
    : multiCount === 1
      ? (options.find(o => o.value === selectedValues[0])?.label ?? '1 selected')
      : `${multiCount} selected`;
  // Closed-state text + whether a value exists (unifies single + multi).
  const hasValue = multiSelect ? multiCount > 0 : !!value;
  const closedText = multiSelect ? multiSummary : displayLabel;

  /* ── Derived: filtered options ──────────────────────────────── */
  // A typed search matches text anywhere in the label, the label's INITIALS ("SM" finds
  // "Sat Mehta" — the quick way to pick a person or business by keyboard), or the
  // option's other words (`keywords`: "resta" finds a fast-food type).
  const needle = search.trim().toLowerCase();
  const textMatched = needle
    ? options.filter(o => {
        const label = o.label.toLowerCase();
        if (label.includes(needle)) return true;
        // at the START of a keyword word only: "uber" must not find "youtuber"
        if (needle.length >= 2 && o.keywords
          && ` ${o.keywords.toLowerCase().replace(/[,\s]+/g, ' ')}`.includes(` ${needle}`)) return true;
        if (needle.length < 2 || /\s/.test(needle)) return false;
        const initials = label.split(/[^a-z0-9]+/).filter(Boolean).map(w => w[0]).join('');
        return initials.startsWith(needle);
      })
    : options;
  const customRank = needle && rankOption
    ? new Map(options.map(o => [o, rankOption(o, search)] as const))
    : null;
  const matched = customRank ? textMatched.filter(o => customRank.get(o)! < Infinity) : textMatched;
  // In a FLAT list the matches that START with what was typed come first ("ele" lists
  // "Electricity" before "Telephone"; a leading account number is skipped, so "1000
  // Chequing" starts with "che" too), then those where a word or an other-word starts
  // with it, then the rest. Grouped and indented lists keep their own order.
  const flatList = !matched.some(o => o.group || (o.depth || 0) > 0);
  const startRank = (o: DropdownOption): number => {
    const label = o.label.toLowerCase();
    if (label.startsWith(needle) || label.replace(/^[\d.-]+\s+/, '').startsWith(needle)) return 0;
    const words = ` ${label} ${(o.keywords || '').toLowerCase()}`.replace(/[,\s]+/g, ' ');
    return words.includes(` ${needle}`) ? 1 : 2;
  };
  const filtered = needle && flatList
    ? matched.map((o, i) => ({ o, i, r: startRank(o) })).sort((x, y) => (customRank ? customRank.get(x.o)! - customRank.get(y.o)! : 0) || x.r - y.r || x.i - y.i).map(x => x.o)
    : matched;
  // "+ Add …" for what was typed, unless it already is an option.
  const typed = search.trim();
  const showCreate = !!createRow && !multiSelect && !!typed && !options.some(o => o.label.trim().toLowerCase() === needle);

  /* ── Close on outside click ─────────────────────────────────── */
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target as Node)) {
        setIsOpen(false);
        setSearch('');
        setHighlightIdx(-1);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  /* ── Open the list where there is room ──────────────────────── */
  useLayoutEffect(() => {
    if (!isOpen) { setPlacement(null); return; }
    const place = () => {
      const el = wrapperRef.current;
      if (!el) return;
      const { above, below } = roomAround(el);
      const up = below < LIST_MAX_PX && above > below;
      const room = up ? above : below;
      setPlacement({ up, maxHeight: Math.max(LIST_MIN_PX, Math.min(LIST_MAX_PX, Math.floor(room))) });
    };
    place();
    window.addEventListener('resize', place);
    window.addEventListener('scroll', place, true);
    return () => {
      window.removeEventListener('resize', place);
      window.removeEventListener('scroll', place, true);
    };
  }, [isOpen]);

  /* ── Scroll highlighted item into view ──────────────────────── */
  useEffect(() => {
    if (highlightIdx >= 0 && listRef.current) {
      const items = listRef.current.querySelectorAll('.sd-option');
      items[highlightIdx]?.scrollIntoView({ block: 'nearest' });
    }
  }, [highlightIdx]);

  /* ── Select a value ─────────────────────────────────────────── */
  const selectValue = useCallback((val: string) => {
    onChange?.(val);
    setIsOpen(false);
    setSearch('');
    setHighlightIdx(-1);
  }, [onChange]);

  /* ── Open the dropdown ──────────────────────────────────────── */
  // `seed` pre-fills the search box so a keyboard user can Tab in and start
  // typing immediately (the first character opens + filters). Empty = plain open.
  const openWithSearch = useCallback((seed: string) => {
    if (disabled) return;
    setIsOpen(true);
    setSearch(seed);
    setHighlightIdx(seed ? 0 : -1);
    setTimeout(() => inputRef.current?.focus(), 0);
  }, [disabled]);
  const open = useCallback(() => openWithSearch(''), [openWithSearch]);

  /* ── Close, optionally keeping keyboard focus on the field ──── */
  const close = useCallback((keepFocus: boolean) => {
    setIsOpen(false);
    setSearch('');
    setHighlightIdx(-1);
    if (keepFocus) {
      // The search input unmounts on close; put focus back on the closed field so
      // Enter "accepts and stays", and the next Tab moves on from here.
      setTimeout(() => (wrapperRef.current?.querySelector('.sd-display') as HTMLElement | null)?.focus(), 0);
    }
  }, []);

  /* ── Commit the highlighted match of a TYPED search ─────────── */
  // Multi-select ADDS it (never un-ticks an owner that is already chosen).
  const commitTyped = useCallback((): boolean => {
    if (!needle || highlightIdx < 0 || highlightIdx >= filtered.length) return false;
    const val = filtered[highlightIdx].value;
    if (multiSelect) { if (!selectedValues.includes(val)) onToggleValue?.(val); }
    else onChange?.(val);
    return true;
  }, [needle, highlightIdx, filtered, multiSelect, selectedValues, onToggleValue, onChange]);

  /* ── Keyboard navigation ────────────────────────────────────── */
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setHighlightIdx(prev => (prev < filtered.length - 1 ? prev + 1 : 0));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHighlightIdx(prev => (prev > 0 ? prev - 1 : filtered.length - 1));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      // Enter = accept and STAY on the field.
      if (needle) {
        // Typed a search ("SM", "Sat"): take the highlighted match and close. More
        // owners/folders are added by opening the list again and picking them.
        if (commitTyped()) close(true);
        else if (showCreate && filtered.length === 0) { createRow!.onCreate(typed); close(true); }
      } else if (highlightIdx >= 0 && highlightIdx < filtered.length) {
        if (multiSelect) {
          onToggleValue?.(filtered[highlightIdx].value);   // browsing the list: toggle
          if (closeOnSelect) close(true);                  // close on pick (reopen to add more)
        } else {
          selectValue(filtered[highlightIdx].value);
          close(true);
        }
      }
    } else if (e.key === 'Tab') {
      // Tab = accept the typed match AND move on. The close is deferred so the browser
      // moves focus from the search input first (it unmounts when the list closes).
      commitTyped();
      setTimeout(() => close(false), 0);
    } else if (e.key === 'Escape') {
      close(true);
    } else if (e.key === 'F2' && onEditOption) {
      const opt = filtered[highlightIdx];
      if (opt?.editable) { e.preventDefault(); close(false); onEditOption(opt.value); }
    }
  };

  /* ── Build grouped rendering ────────────────────────────────── */
  const renderOptions = () => {
    if (filtered.length === 0) {
      return showCreate ? null : <div className="sd-no-results">No matches found</div>;
    }

    const hasGroups = filtered.some(o => o.group);

    if (hasGroups) {
      // Render grouped options
      const groups: { label: string; items: { opt: DropdownOption; globalIdx: number }[] }[] = [];
      const ungrouped: { opt: DropdownOption; globalIdx: number }[] = [];
      let globalIdx = 0;

      filtered.forEach(opt => {
        if (opt.group) {
          let grp = groups.find(g => g.label === opt.group);
          if (!grp) {
            grp = { label: opt.group!, items: [] };
            groups.push(grp);
          }
          grp.items.push({ opt, globalIdx });
        } else {
          ungrouped.push({ opt, globalIdx });
        }
        globalIdx++;
      });

      return (
        <>
          {ungrouped.map(({ opt, globalIdx: gi }) => renderSingleOption(opt, gi))}
          {groups.map(grp => (
            <React.Fragment key={grp.label}>
              <div className="sd-group-label">{grp.label}</div>
              {grp.items.map(({ opt, globalIdx: gi }) => renderSingleOption(opt, gi))}
            </React.Fragment>
          ))}
        </>
      );
    }

    // No groups — flat list
    return filtered.map((opt, idx) => renderSingleOption(opt, idx));
  };

  const renderSingleOption = (opt: DropdownOption, idx: number) => {
    const isHighlighted = idx === highlightIdx;
    const isSelected = isOptionSelected(opt.value);
    const indent = opt.depth ? opt.depth * 16 : 0;

    return (
      <div
        key={opt.value}
        className={`sd-option ${multiSelect ? 'sd-option-multi' : ''} ${onEditOption && opt.editable ? 'sd-option-editable' : ''} ${isHighlighted ? 'sd-option-highlighted' : ''} ${isSelected ? 'sd-option-selected' : ''}`}
        style={indent ? { paddingLeft: 12 + indent } : undefined}
        onMouseEnter={() => setHighlightIdx(idx)}
        onMouseDown={(e) => {
          e.preventDefault(); // keep focus on input
          if (multiSelect) {
            onToggleValue?.(opt.value);
            if (closeOnSelect) close(true);              // close on pick (reopen to add more)
            else setTimeout(() => inputRef.current?.focus(), 0);  // stay open to add more
          } else {
            selectValue(opt.value);
          }
        }}
      >
        {multiSelect ? (
          <>
            <span className="sd-option-label">{opt.label}</span>
            {isSelected && <span className="sd-option-check">✓</span>}
          </>
        ) : onEditOption && opt.editable ? (
          <>
            <span className="sd-option-label">{opt.label}</span>
            <button
              type="button"
              className="sd-option-edit"
              tabIndex={-1}
              title={`Edit “${opt.label}” (F2)`}
              aria-label={`Edit ${opt.label}`}
              onMouseDown={(e) => {
                e.preventDefault();
                e.stopPropagation();   // not a pick
                close(false);
                onEditOption(opt.value);
              }}
            >
              <FiEdit2 size={13} />
            </button>
          </>
        ) : opt.label}
      </div>
    );
  };

  /* ── Render ─────────────────────────────────────────────────── */
  return (
    <div
      className={`sd-wrapper ${wrapperClassName} ${hasError ? 'sd-error' : ''} ${disabled ? 'sd-disabled' : ''} ${isOpen && placement?.up ? 'sd-open-up' : ''}`}
      ref={wrapperRef}
    >
      {/* Closed state: looks like a select */}
      {!isOpen ? (
        <div
          className={`sd-display ${className}`}
          onClick={open}
          tabIndex={disabled ? -1 : 0}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ' || e.key === 'ArrowDown') {
              e.preventDefault();
              open();
            } else if (e.key.length === 1 && !e.ctrlKey && !e.metaKey && !e.altKey) {
              // Type-to-search: any printable character opens the dropdown and seeds
              // the filter with it, so a keyboard user who Tabs in can just start typing.
              e.preventDefault();
              openWithSearch(e.key);
            }
          }}
          role="combobox"
          aria-expanded={isOpen}
          aria-haspopup="listbox"
        >
          <span className={hasValue ? 'sd-display-text' : 'sd-display-placeholder'}>
            {hasValue ? closedText : placeholder}
          </span>
          <span className="sd-chevron">▾</span>
        </div>
      ) : (
        /* Open state: text input with dropdown */
        <>
          <div className={`sd-input-row ${className}`}>
            <input
              ref={inputRef}
              autoFocus   /* focus as it mounts, so the 2nd typed character is never dropped */
              className="sd-search-input"
              type="text"
              value={search}
              onChange={e => { setSearch(e.target.value); setHighlightIdx(0); }}
              onKeyDown={handleKeyDown}
              placeholder={hasValue ? closedText : placeholder}
              autoComplete="off"
            />
            {/* Clear / close button (single-select only) */}
            {!multiSelect && value && (
              <button
                className="sd-clear-btn"
                onMouseDown={(e) => {
                  e.preventDefault();
                  selectValue('');
                }}
                title="Clear selection"
                type="button"
              >
                ✕
              </button>
            )}
            <span className="sd-chevron sd-chevron-open">▴</span>
          </div>
          {/* tabIndex -1: a scrollable list is a Tab stop in current browsers; Tab from the
              search box must land on the NEXT FIELD, not on the list that is about to close. */}
          <div
            className={`sd-dropdown ${placement?.up ? 'sd-dropdown-up' : ''}`}
            ref={listRef}
            tabIndex={-1}
            style={placement ? { maxHeight: placement.maxHeight } : undefined}
          >
            {actionRow && (
              <div
                className="sd-action-row"
                onMouseDown={(e) => {
                  e.preventDefault();          // keep focus off; don't toggle an option
                  setIsOpen(false);
                  setSearch('');
                  setHighlightIdx(-1);
                  actionRow.onClick();
                }}
              >
                {actionRow.label}
              </div>
            )}
            {showCreate && (
              <div
                className="sd-action-row sd-create-row"
                onMouseDown={(e) => {
                  e.preventDefault();
                  const value = typed;
                  setIsOpen(false);
                  setSearch('');
                  setHighlightIdx(-1);
                  createRow!.onCreate(value);
                }}
              >
                {createRow!.label(typed)}
              </div>
            )}
            {renderOptions()}
          </div>
        </>
      )}
    </div>
  );
};

export default SearchableDropdown;
